import { AltitudeMode, ColorMode, DisplayMode, IconState, KMLElementType, ListItemType, StyleState, } from "./data";
import { OptionsManager } from "../../options";
/**
 * KMLParserSession represents a single parsing session
 */
export class KMLParserSession {
    constructor() {
        /**
         * Internal container for storing the bag of all elements with an ID
         */
        this.objectsById = new Map();
    }
    parseText(text) {
        const document = KMLParserSession._domParser.parseFromString(insertNamespaces(text), "application/xml");
        return this.parseDocument(document);
    }
    parseDocument(doc) {
        const kmlElement = findChildElementByName(doc, "kml");
        if (kmlElement === null)
            throw new Error("KML file missing kml element");
        const root = this.parseRoot(kmlElement);
        this.root = root;
        return root;
    }
    parseRoot(element) {
        let foundContainer;
        for (let i = 0; i < element.children.length; i += 1) {
            const child = element.children.item(i);
            if (child === null)
                continue;
            const elementType = elementTypeFromTagName(child);
            if (elementType === undefined)
                continue;
            if (isContainerElement(elementType)) {
                if (foundContainer !== undefined) {
                    throw new Error("KML file has multiple root containers");
                }
                foundContainer = child;
            }
        }
        if (foundContainer !== undefined) {
            return this.parseContainer(foundContainer);
        }
        else {
            return undefined;
        }
    }
    parseContainer(element) {
        const elementType = elementTypeFromTagName(element);
        if (elementType === undefined)
            throw new Error("Unknown element type");
        if (!isContainerElement(elementType)) {
            throw new Error("Unknown container element type");
        }
        const container = {
            elementType,
            visibility: true,
            open: false,
            features: [],
            styleSelectors: [],
            isContainerElement: true,
            isFeatureElement: true,
            isGeometryElement: false,
            isStyleSelector: false,
            isColorStyle: false,
        };
        for (let i = 0; i < element.children.length; i += 1) {
            const child = element.children.item(i);
            if (child === null)
                continue;
            this.tryLoadFeatureField(container, child);
            this.tryLoadContainerField(container, child);
        }
        if (element.id.length !== 0) {
            this.objectsById.set(element.id, container);
        }
        return container;
    }
    parseFeature(element) {
        const elementType = elementTypeFromTagName(element);
        if (elementType === undefined)
            throw new Error("Unknown element type");
        if (!isFeatureElement(elementType)) {
            throw new Error("Unknown feature element type");
        }
        if (isContainerElement(elementType)) {
            return this.parseContainer(element);
        }
        let feature;
        switch (elementType) {
            case KMLElementType.Placemark:
                const placemark = {
                    elementType,
                    visibility: true,
                    open: false,
                    geometries: [],
                    styleSelectors: [],
                    isContainerElement: false,
                    isFeatureElement: true,
                    isGeometryElement: false,
                    isStyleSelector: false,
                    isColorStyle: false,
                };
                feature = placemark;
                break;
            default:
                throw new Error("Unreachable");
        }
        for (let i = 0; i < element.children.length; i += 1) {
            const child = element.children.item(i);
            if (child === null)
                continue;
            this.tryLoadFeatureField(feature, child);
            if (feature.elementType === KMLElementType.Placemark) {
                this.tryLoadPlacemarkField(feature, child);
            }
        }
        if (element.id.length !== 0) {
            this.objectsById.set(element.id, feature);
        }
        return feature;
    }
    parseGeometry(element) {
        const elementType = elementTypeFromTagName(element);
        if (elementType === undefined)
            throw new Error("Unknown element type");
        if (!isGeometryElement(elementType)) {
            throw new Error("Unknown feature element type");
        }
        switch (elementType) {
            case KMLElementType.Point:
                return this.parsePoint(element);
            case KMLElementType.LineString:
                return this.parseLineString(element);
            case KMLElementType.LinearRing:
                return this.parseLinearRing(element);
            case KMLElementType.Polygon:
                return this.parsePolygon(element);
            case KMLElementType.MultiGeometry:
                return this.parseMultiGeometry(element);
            default:
                throw new Error("Unreachable");
        }
    }
    parseStyleSelector(element) {
        const elementType = elementTypeFromTagName(element);
        if (elementType === undefined)
            throw new Error("Unknown element type");
        if (!isStyleSelectorElement(elementType)) {
            throw new Error("Unknown feature element type");
        }
        switch (elementType) {
            case KMLElementType.Style:
                return this.parseStyle(element);
            case KMLElementType.StyleMap:
                return this.parseStyleMap(element);
            default:
                throw new Error("Unreachable");
        }
    }
    parsePoint(element) {
        const elementType = elementTypeFromTagName(element);
        if (elementType === undefined)
            throw new Error("Unknown element type");
        if (elementType !== KMLElementType.Point) {
            throw new Error(`Attempt to parse a '${element.tagName}' element as a Point`);
        }
        const point = {
            elementType: KMLElementType.Point,
            coordinates: [0, 0, 0],
            isContainerElement: false,
            isFeatureElement: false,
            isGeometryElement: true,
            isStyleSelector: false,
            isColorStyle: false,
        };
        for (let i = 0; i < element.children.length; i += 1) {
            const child = element.children.item(i);
            if (child === null)
                continue;
            this.tryLoadExtrudeAltitudeMode(point, child);
            const receiver = {
                coordinates: [],
            };
            this.tryLoadMultipleCoordinate(receiver, child, 1);
            if (receiver.coordinates.length === 1) {
                point.coordinates = receiver.coordinates[0];
            }
        }
        if (element.id.length !== 0) {
            this.objectsById.set(element.id, point);
        }
        return point;
    }
    parseLineString(element) {
        const elementType = elementTypeFromTagName(element);
        if (elementType === undefined)
            throw new Error("Unknown element type");
        if (elementType !== KMLElementType.LineString) {
            throw new Error(`Attempt to parse a '${element.tagName}' element as a LineString`);
        }
        const lineString = {
            elementType: KMLElementType.LineString,
            coordinates: [],
            isContainerElement: false,
            isFeatureElement: false,
            isGeometryElement: true,
            isStyleSelector: false,
            isColorStyle: false,
        };
        for (let i = 0; i < element.children.length; i += 1) {
            const child = element.children.item(i);
            if (child === null)
                continue;
            this.tryLoadExtrudeAltitudeMode(lineString, child);
            this.tryLoadTessellate(lineString, child);
            this.tryLoadMultipleCoordinate(lineString, child);
        }
        if (lineString.coordinates.length === 0) {
            return undefined;
        }
        if (lineString.coordinates.length < 2) {
            return undefined;
        }
        if (element.id.length !== 0) {
            this.objectsById.set(element.id, lineString);
        }
        return lineString;
    }
    parseLinearRing(element) {
        const elementType = elementTypeFromTagName(element);
        if (elementType === undefined)
            throw new Error("Unknown element type");
        if (elementType !== KMLElementType.LinearRing) {
            throw new Error(`Attempt to parse a '${element.tagName}' element as a LinearRing`);
        }
        const linearRing = {
            elementType: KMLElementType.LinearRing,
            coordinates: [],
            isContainerElement: false,
            isFeatureElement: false,
            isGeometryElement: true,
            isStyleSelector: false,
            isColorStyle: false,
        };
        for (let i = 0; i < element.children.length; i += 1) {
            const child = element.children.item(i);
            if (child === null)
                continue;
            this.tryLoadExtrudeAltitudeMode(linearRing, child);
            this.tryLoadTessellate(linearRing, child);
            this.tryLoadMultipleCoordinate(linearRing, child);
        }
        if (linearRing.coordinates.length === 0) {
            return undefined;
        }
        if (linearRing.coordinates.length < 2) {
            return undefined;
        }
        if (element.id.length !== 0) {
            this.objectsById.set(element.id, linearRing);
        }
        return linearRing;
    }
    parsePolygon(element) {
        const elementType = elementTypeFromTagName(element);
        if (elementType === undefined)
            throw new Error("Unknown element type");
        if (elementType !== KMLElementType.Polygon) {
            throw new Error(`Attempt to parse a '${element.tagName}' element as a Polygon`);
        }
        const polygon = {
            elementType: KMLElementType.Polygon,
            // @ts-ignore
            outerRing: undefined,
            innerRings: [],
            isContainerElement: false,
            isFeatureElement: false,
            isGeometryElement: true,
        };
        for (let i = 0; i < element.children.length; i += 1) {
            const child = element.children.item(i);
            if (child === null)
                continue;
            this.tryLoadExtrudeAltitudeMode(polygon, child);
            this.tryLoadTessellate(polygon, child);
            this.tryLoadPolygonField(polygon, child);
        }
        if (polygon.outerRing === undefined) {
            return undefined;
        }
        if (element.id.length !== 0) {
            this.objectsById.set(element.id, polygon);
        }
        return polygon;
    }
    parseMultiGeometry(element) {
        const elementType = elementTypeFromTagName(element);
        if (elementType === undefined)
            throw new Error("Unknown element type");
        if (elementType !== KMLElementType.MultiGeometry) {
            throw new Error(`Attempt to parse a '${element.tagName}' element as a MultiGeometry`);
        }
        const multiGeometry = {
            elementType: KMLElementType.MultiGeometry,
            geometries: [],
            isContainerElement: false,
            isFeatureElement: false,
            isGeometryElement: true,
            isStyleSelector: false,
            isColorStyle: false,
        };
        for (let i = 0; i < element.children.length; i += 1) {
            const child = element.children.item(i);
            if (child === null)
                continue;
            this.tryLoadMultiGeometryField(multiGeometry, child);
        }
        if (multiGeometry.geometries.length === 0) {
            return undefined;
        }
        if (element.id.length !== 0) {
            this.objectsById.set(element.id, multiGeometry);
        }
        return multiGeometry;
    }
    parseStyle(element) {
        const elementType = elementTypeFromTagName(element);
        if (elementType === undefined)
            throw new Error("Unknown element type");
        if (elementType !== KMLElementType.Style) {
            throw new Error(`Attempt to parse a '${element.tagName}' element as a Style`);
        }
        const style = {
            elementType: KMLElementType.Style,
            isContainerElement: false,
            isFeatureElement: false,
            isGeometryElement: false,
            isStyleSelector: true,
            isColorStyle: false,
        };
        for (let i = 0; i < element.children.length; i += 1) {
            const child = element.children.item(i);
            if (child === null)
                continue;
            this.tryLoadStyleField(style, child);
        }
        if (element.id.length !== 0) {
            this.objectsById.set(element.id, style);
        }
        return style;
    }
    parseStyleMap(element) {
        const elementType = elementTypeFromTagName(element);
        if (elementType === undefined)
            throw new Error("Unknown element type");
        if (elementType !== KMLElementType.StyleMap) {
            throw new Error(`Attempt to parse a '${element.tagName}' element as a StyleMap`);
        }
        const styleMap = {
            elementType: KMLElementType.StyleMap,
            map: new Map(),
            isContainerElement: false,
            isFeatureElement: false,
            isGeometryElement: false,
            isStyleSelector: true,
            isColorStyle: false,
        };
        for (let i = 0; i < element.children.length; i += 1) {
            const child = element.children.item(i);
            if (child === null)
                continue;
            this.tryLoadStyleMapField(styleMap, child);
        }
        if (element.id.length !== 0) {
            this.objectsById.set(element.id, styleMap);
        }
        return styleMap;
    }
    parseLineStyle(element) {
        const elementType = elementTypeFromTagName(element);
        if (elementType === undefined)
            throw new Error("Unknown element type");
        if (elementType !== KMLElementType.LineStyle) {
            throw new Error(`Attempt to parse a '${element.tagName}' element as a LineStyle`);
        }
        const style = {
            elementType: KMLElementType.LineStyle,
            color: [1, 1, 1, 1],
            colorMode: ColorMode.Normal,
            width: 1,
            isColorStyle: true,
            isContainerElement: false,
            isFeatureElement: false,
            isGeometryElement: false,
            isStyleSelector: false,
        };
        for (let i = 0; i < element.children.length; i += 1) {
            const child = element.children.item(i);
            if (child === null)
                continue;
            this.tryLoadColorStyleField(style, child);
            this.tryLoadLineStyleField(style, child);
        }
        if (element.id.length !== 0) {
            this.objectsById.set(element.id, style);
        }
        return style;
    }
    parsePolyStyle(element) {
        const elementType = elementTypeFromTagName(element);
        if (elementType === undefined)
            throw new Error("Unknown element type");
        if (elementType !== KMLElementType.PolyStyle) {
            throw new Error(`Attempt to parse a '${element.tagName}' element as a PolyStyle`);
        }
        const style = {
            elementType: KMLElementType.PolyStyle,
            color: [1, 1, 1, 1],
            colorMode: ColorMode.Normal,
            fill: true,
            outline: true,
            isColorStyle: true,
            isContainerElement: false,
            isFeatureElement: false,
            isGeometryElement: false,
            isStyleSelector: false,
        };
        for (let i = 0; i < element.children.length; i += 1) {
            const child = element.children.item(i);
            if (child === null)
                continue;
            this.tryLoadColorStyleField(style, child);
            this.tryLoadPolyStyleField(style, child);
        }
        if (element.id.length !== 0) {
            this.objectsById.set(element.id, style);
        }
        return style;
    }
    parseIconStyle(element) {
        const elementType = elementTypeFromTagName(element);
        if (elementType === undefined)
            throw new Error("Unknown element type");
        if (elementType !== KMLElementType.IconStyle) {
            throw new Error(`Attempt to parse a '${element.tagName}' element as a IconStyle`);
        }
        const style = {
            elementType: KMLElementType.IconStyle,
            color: [1, 1, 1, 1],
            colorMode: ColorMode.Normal,
            heading: 0,
            scale: 0,
            hotSpot: {
                x: 0,
                y: 0,
                xUnits: "pixels",
                yUnits: "pixels",
            },
            icon: "",
            isColorStyle: true,
            isContainerElement: false,
            isFeatureElement: false,
            isGeometryElement: false,
            isStyleSelector: false,
        };
        for (let i = 0; i < element.children.length; i += 1) {
            const child = element.children.item(i);
            if (child === null)
                continue;
            this.tryLoadColorStyleField(style, child);
            this.tryLoadIconStyleField(style, child);
        }
        if (element.id.length !== 0) {
            this.objectsById.set(element.id, style);
        }
        return style;
    }
    parseLabelStyle(element) {
        const elementType = elementTypeFromTagName(element);
        if (elementType === undefined)
            throw new Error("Unknown element type");
        if (elementType !== KMLElementType.LabelStyle) {
            throw new Error(`Attempt to parse a '${element.tagName}' element as a LabelStyle`);
        }
        const style = {
            elementType: KMLElementType.LabelStyle,
            color: [1, 1, 1, 1],
            colorMode: ColorMode.Normal,
            scale: 1,
            isColorStyle: true,
            isContainerElement: false,
            isFeatureElement: false,
            isGeometryElement: false,
            isStyleSelector: false,
        };
        for (let i = 0; i < element.children.length; i += 1) {
            const child = element.children.item(i);
            if (child === null)
                continue;
            this.tryLoadColorStyleField(style, child);
            this.tryLoadLabelStyleField(style, child);
        }
        return style;
    }
    parseBalloonStyle(element) {
        const elementType = elementTypeFromTagName(element);
        if (elementType === undefined)
            throw new Error("Unknown element type");
        if (elementType !== KMLElementType.BalloonStyle) {
            throw new Error(`Attempt to parse a '${element.tagName}' element as a BalloonStyle`);
        }
        const style = {
            elementType: KMLElementType.BalloonStyle,
            bgColor: [1, 1, 1, 1],
            textColor: [0, 0, 0, 1],
            displayMode: DisplayMode.Default,
            text: "",
            isColorStyle: true,
            isContainerElement: false,
            isFeatureElement: false,
            isGeometryElement: false,
            isStyleSelector: false,
        };
        for (let i = 0; i < element.children.length; i += 1) {
            const child = element.children.item(i);
            if (child === null)
                continue;
            this.tryLoadBalloonStyleField(style, child);
        }
        if (element.id.length !== 0) {
            this.objectsById.set(element.id, style);
        }
        return style;
    }
    parseListStyle(element) {
        const elementType = elementTypeFromTagName(element);
        if (elementType === undefined)
            throw new Error("Unknown element type");
        if (elementType !== KMLElementType.ListStyle) {
            throw new Error(`Attempt to parse a '${element.tagName}' element as a ListStyle`);
        }
        const style = {
            elementType: KMLElementType.ListStyle,
            bgColor: [0, 0, 0, 0],
            listItemType: ListItemType.Check,
            icons: [],
            isColorStyle: true,
            isContainerElement: false,
            isFeatureElement: false,
            isGeometryElement: false,
            isStyleSelector: false,
        };
        for (let i = 0; i < element.children.length; i += 1) {
            const child = element.children.item(i);
            if (child === null)
                continue;
            this.tryLoadListStyleField(style, child);
        }
        if (element.id.length !== 0) {
            this.objectsById.set(element.id, style);
        }
        return style;
    }
    tryLoadFeatureField(feature, field) {
        const elementType = elementTypeFromTagName(field);
        if (elementType !== undefined) {
            if (isStyleSelectorElement(elementType)) {
                feature.styleSelectors.push(this.parseStyleSelector(field));
            }
        }
        switch (field.tagName) {
            case "name": {
                feature.name = field.textContent || undefined;
                break;
            }
            case "visibility": {
                feature.visibility = readBooleanTextContent(field.textContent);
                break;
            }
            case "open": {
                feature.open = readBooleanTextContent(field.textContent);
                break;
            }
            case "styleUrl": {
                feature.styleUri = field.textContent !== null ? field.textContent.trim() : undefined;
                break;
            }
            case "Region": {
                const region = feature.region; // Hack for type checking
                let box = null;
                let lod = null;
                for (let i = 0; i < field.children.length; i += 1) {
                    const child = field.children.item(i);
                    if (child === null)
                        continue;
                    switch (child.tagName) {
                        case "LatLonAltBox": {
                            box = {
                                north: 0,
                                south: 0,
                                east: 0,
                                west: 0,
                                minAlt: 0,
                                maxAlt: 0,
                                altitudeMode: AltitudeMode.ClampToGround,
                            };
                            for (let j = 0; j < child.children.length; j += 1) {
                                const childField = child.children.item(j);
                                if (childField === null)
                                    continue;
                                switch (childField.tagName) {
                                    case "north": {
                                        box.north = readFloat(childField.textContent);
                                        break;
                                    }
                                    case "south": {
                                        box.south = readFloat(childField.textContent);
                                        break;
                                    }
                                    case "east": {
                                        box.east = readFloat(childField.textContent);
                                        break;
                                    }
                                    case "west": {
                                        box.west = readFloat(childField.textContent);
                                        break;
                                    }
                                    case "minAltitude": {
                                        box.minAlt = readFloat(childField.textContent);
                                        break;
                                    }
                                    case "maxAltitude": {
                                        box.maxAlt = readFloat(childField.textContent);
                                        break;
                                    }
                                    case "altitudeMode": {
                                        box.altitudeMode = readAltitudeModeTextContent(childField.textContent);
                                    }
                                }
                            }
                            break;
                        }
                        case "Lod": {
                            lod = {
                                minLodPixels: 0,
                                maxLodPixels: -1,
                            };
                            for (let j = 0; j < child.children.length; j += 1) {
                                const childField = child.children.item(j);
                                if (childField === null)
                                    continue;
                                switch (childField.tagName) {
                                    case "minLodPixels": {
                                        lod.minLodPixels = readFloat(childField.textContent);
                                        break;
                                    }
                                    case "maxLodPixels": {
                                        lod.maxLodPixels = readFloat(childField.textContent);
                                        break;
                                    }
                                    case "minFadeExtent": {
                                        lod.minFadeExtent = readFloat(childField.textContent);
                                        break;
                                    }
                                    case "maxFadeExtent": {
                                        lod.maxFadeExtent = readFloat(childField.textContent);
                                        break;
                                    }
                                }
                            }
                            break;
                        }
                    }
                }
                if (box === null) {
                    throw new Error("LatLonAltBox field missing on Region");
                }
                feature.region = {
                    box,
                    lod: lod || undefined,
                };
                break;
            }
        }
    }
    tryLoadExtrudeAltitudeMode(point, field) {
        switch (field.tagName) {
            case "extrude": {
                point.extrude = readBooleanTextContent(field.textContent);
                break;
            }
            case "altitudeMode": {
                point.altitudeMode = readAltitudeModeTextContent(field.textContent);
                break;
            }
        }
    }
    tryLoadTessellate(point, field) {
        switch (field.tagName) {
            case "tessellate": {
                point.tessellate = readBooleanTextContent(field.textContent);
                break;
            }
        }
    }
    tryLoadMultipleCoordinate(obj, field, maxTuples) {
        switch (field.tagName) {
            case "coordinates": {
                obj.coordinates = readMultiLatLongAltTuple(field.textContent, maxTuples);
                break;
            }
        }
    }
    tryLoadContainerField(feature, field) {
        const elementType = elementTypeFromTagName(field);
        if (elementType !== undefined) {
            if (isFeatureElement(elementType)) {
                feature.features.push(this.parseFeature(field));
            }
        }
    }
    tryLoadPlacemarkField(placemark, field) {
        const elementType = elementTypeFromTagName(field);
        if (elementType !== undefined) {
            if (isGeometryElement(elementType)) {
                // Silently ignore busted geometry items. Not my fault people can't follow spec
                const geom = this.parseGeometry(field);
                if (geom !== undefined) {
                    placemark.geometries.push(geom);
                }
            }
        }
    }
    tryLoadPolygonField(polygon, field) {
        switch (field.tagName) {
            case "outerBoundaryIs": {
                for (let i = 0; i < field.children.length; i += 1) {
                    const child = field.children.item(i);
                    if (child === null)
                        continue;
                    const elementType = elementTypeFromTagName(child);
                    if (elementType === KMLElementType.LinearRing) {
                        if (polygon.outerRing !== undefined) {
                            throw new Error("Multiple LinearRing elements defined in outerBoundaryIs");
                        }
                        else {
                            const ring = this.parseLinearRing(child);
                            if (ring === undefined) {
                                throw new Error("Polygon outerBoundaryIs contains broken LinearRing");
                            }
                            polygon.outerRing = ring;
                        }
                        break;
                    }
                }
                break;
            }
            case "innerBoundaryIs": {
                for (let i = 0; i < field.children.length; i += 1) {
                    const child = field.children.item(i);
                    if (child === null)
                        continue;
                    const elementType = elementTypeFromTagName(child);
                    if (elementType === KMLElementType.LinearRing) {
                        const ring = this.parseLinearRing(child);
                        if (ring === undefined) {
                            throw new Error("Polygon innerBoundaryIs contains broken LinearRing");
                        }
                        polygon.innerRings.push(ring);
                        break;
                    }
                }
                break;
            }
        }
    }
    tryLoadMultiGeometryField(feature, field) {
        const elementType = elementTypeFromTagName(field);
        if (elementType !== undefined) {
            if (isGeometryElement(elementType)) {
                // Silently ignore busted geometry items. Not my fault people can't follow spec
                const geom = this.parseGeometry(field);
                if (geom !== undefined) {
                    feature.geometries.push(geom);
                }
            }
            else {
                throw new Error(`Unexpected '${field.tagName}' in MultiGeometry element`);
            }
        }
    }
    tryLoadStyleField(style, field) {
        const elementType = elementTypeFromTagName(field);
        if (elementType !== undefined) {
            switch (elementType) {
                case KMLElementType.LineStyle:
                    style.lineStyle = this.parseLineStyle(field);
                    break;
                case KMLElementType.PolyStyle:
                    style.polyStyle = this.parsePolyStyle(field);
                    break;
                case KMLElementType.IconStyle:
                    style.iconStyle = this.parseIconStyle(field);
                    break;
                case KMLElementType.LabelStyle:
                    style.labelStyle = this.parseLabelStyle(field);
                    break;
                case KMLElementType.BalloonStyle:
                    style.balloonStyle = this.parseBalloonStyle(field);
                    break;
                case KMLElementType.ListStyle:
                    style.listStyle = this.parseListStyle(field);
                    break;
            }
        }
    }
    tryLoadColorStyleField(obj, field) {
        switch (field.tagName) {
            case "color": {
                obj.color = readColor(field.textContent);
                break;
            }
            case "colorMode": {
                obj.colorMode = readColorMode(field.textContent);
                break;
            }
        }
    }
    tryLoadLineStyleField(obj, field) {
        switch (field.tagName) {
            case "width": {
                obj.width = readFloat(field.textContent);
                break;
            }
        }
    }
    tryLoadPolyStyleField(obj, field) {
        switch (field.tagName) {
            case "fill": {
                obj.fill = readBooleanTextContent(field.textContent);
                break;
            }
            case "outline": {
                obj.outline = readBooleanTextContent(field.textContent);
                break;
            }
        }
    }
    tryLoadIconStyleField(obj, field) {
        switch (field.tagName) {
            case "scale": {
                obj.scale = readFloat(field.textContent);
                break;
            }
            case "heading": {
                obj.heading = readFloat(field.textContent);
                break;
            }
            case "Icon": {
                const child = field.children.namedItem("href");
                if (child !== null && child.textContent !== null) {
                    obj.icon = child.textContent.trim();
                }
                break;
            }
            case "hotSpot": {
                const xAttr = field.attributes.getNamedItem("x");
                const yAttr = field.attributes.getNamedItem("y");
                const xUnitsAttr = field.attributes.getNamedItem("xunits");
                const yUnitsAttr = field.attributes.getNamedItem("yunits");
                function check(v) {
                    return v !== null && v.textContent !== null;
                }
                if (check(xAttr) && check(yAttr) && check(xUnitsAttr) && check(yUnitsAttr)) {
                    obj.hotSpot.x = assertNaN(parseFloat(xAttr.textContent.trim()));
                    obj.hotSpot.y = assertNaN(parseFloat(yAttr.textContent.trim()));
                    obj.hotSpot.xUnits = readHotSpotUnits(xUnitsAttr.textContent.trim());
                    obj.hotSpot.yUnits = readHotSpotUnits(yUnitsAttr.textContent.trim());
                }
                break;
            }
        }
    }
    tryLoadLabelStyleField(obj, field) {
        switch (field.tagName) {
            case "scale": {
                obj.scale = readFloat(field.textContent);
                break;
            }
        }
    }
    tryLoadBalloonStyleField(obj, field) {
        switch (field.tagName) {
            case "bgColor": {
                obj.bgColor = readColor(field.textContent);
                break;
            }
            case "textColor": {
                obj.textColor = readColor(field.textContent);
                break;
            }
            case "text": {
                obj.text = field.textContent || "";
                break;
            }
            case "displayMode": {
                obj.displayMode = readDisplayMode(field.textContent);
                break;
            }
        }
    }
    tryLoadListStyleField(obj, field) {
        switch (field.tagName) {
            case "bgColor": {
                obj.bgColor = readColor(field.textContent);
                break;
            }
            case "listItemType": {
                obj.listItemType = readListItemType(field.textContent);
                break;
            }
            case "ItemIcon": {
                function check(v) {
                    return v !== null && v.textContent !== null;
                }
                const stateField = field.children.namedItem("state");
                const hrefField = field.children.namedItem("href");
                if (check(stateField) && check(hrefField)) {
                    obj.icons.push({
                        state: readIconState(stateField.textContent.trim()),
                        href: hrefField.textContent.trim(),
                    });
                }
            }
        }
    }
    tryLoadStyleMapField(obj, field) {
        switch (field.tagName) {
            case "Pair": {
                function check(v) {
                    return v !== null && v.textContent !== null;
                }
                let keyField = null;
                let styleUrlField = null;
                let styleField = null;
                for (let i = 0; i < field.children.length; i += 1) {
                    const child = field.children.item(i);
                    if (child === null)
                        continue;
                    switch (child.tagName) {
                        case "key":
                            keyField = child;
                            break;
                        case "styleUrl":
                            styleUrlField = child;
                            break;
                        case "Style":
                            styleField = child;
                            break;
                    }
                }
                if (check(keyField) && check(styleUrlField)) {
                    const key = readStyleState(keyField.textContent);
                    obj.map.set(key, styleUrlField.textContent.trim());
                }
                else if (check(keyField) && styleField !== null) {
                    const key = readStyleState(keyField.textContent);
                    obj.map.set(key, this.parseStyle(styleField));
                }
                break;
            }
        }
    }
}
KMLParserSession._domParser = new DOMParser();
function readBooleanTextContent(text) {
    if (text !== null) {
        switch (text) {
            case "0":
                return false;
            case "1":
                return true;
            default:
                throw new Error(`Invalid KML boolean field value '${text}'`);
        }
    }
    else {
        throw new Error("KML boolean field textContent empty");
    }
}
function readAltitudeModeTextContent(text) {
    if (text !== null) {
        switch (text) {
            case "clampedToGround": // I fucking swear are people capable of reading a format spec!?!?!?!?
            case "clampToGround":
                return AltitudeMode.ClampToGround;
            case "relativeToGround":
                return AltitudeMode.RelativeToGround;
            case "absolute":
                return AltitudeMode.Absolute;
            default:
                throw new Error(`Invalid KML altitudeMode field value '${text}'`);
        }
    }
    else {
        throw new Error("KML altitudeMode field textContent empty");
    }
}
function readSingleLatLongAltTuple(text) {
    if (text !== null) {
        text = text.trim();
        const numMatches = text.split(",");
        if (numMatches === null || numMatches.length < 2)
            throw new Error("KML");
        const x = assertNaN(parseFloat(numMatches[0]));
        const y = assertNaN(parseFloat(numMatches[1]));
        const z = numMatches.length > 2 ? parseFloat(numMatches[2]) : 0;
        return [x, y, z];
    }
    else {
        throw new Error("KML field textContent unexpectedly empty");
    }
}
function readMultiLatLongAltTuple(text, maxTuples) {
    if (text !== null) {
        text = text.trim();
        const tupleMatches = text.split(/\s+/g);
        if (maxTuples !== undefined) {
            tupleMatches.length = maxTuples;
        }
        return tupleMatches.map((v) => {
            const numMatches = v.split(",");
            if (numMatches === null || numMatches.length < 2)
                throw new Error("KML");
            const x = assertNaN(parseFloat(numMatches[0]));
            const y = assertNaN(parseFloat(numMatches[1]));
            const z = numMatches.length > 2 ? assertNaN(parseFloat(numMatches[2])) : 0;
            return [x, y, z];
        });
    }
    else {
        throw new Error("KML field textContent unexpectedly empty");
    }
}
function readColor(text) {
    if (text !== null) {
        text = text.trim();
        if (text.length !== 8)
            throw new Error("KML color field invalid");
        const chunks = text.match(/[\dA-Fa-f]{2}/g);
        if (chunks === null || chunks.length !== 4)
            throw new Error("KML color field invalid");
        const r = parseInt(chunks[3], 16);
        const g = parseInt(chunks[2], 16);
        const b = parseInt(chunks[1], 16);
        const a = parseInt(chunks[0], 16);
        return [r, g, b, a];
    }
    else {
        throw new Error("KML field textContent unexpectedly empty");
    }
}
function readColorMode(text) {
    if (text !== null) {
        switch (text) {
            case "normal":
                return ColorMode.Normal;
            case "random":
                return ColorMode.Random;
            default:
                return ColorMode.Normal;
        }
    }
    else {
        return ColorMode.Normal;
    }
}
function readDisplayMode(text) {
    if (text !== null) {
        switch (text) {
            case "default":
                return DisplayMode.Default;
            case "hide":
                return DisplayMode.Hide;
            default:
                return DisplayMode.Default;
        }
    }
    else {
        return DisplayMode.Default;
    }
}
function readListItemType(text) {
    if (text !== null) {
        switch (text) {
            case "check":
                return ListItemType.Check;
            case "checkOffOnly":
                return ListItemType.CheckOffOnly;
            case "checkHideChildren":
                return ListItemType.CheckHideChildren;
            case "radioFolder":
                return ListItemType.RadioFolder;
            default:
                return ListItemType.Check;
        }
    }
    else {
        return ListItemType.Check;
    }
}
function readHotSpotUnits(text) {
    if (text !== null) {
        switch (text) {
            case "fraction":
                return "fraction";
            case "pixels":
                return "pixels";
            case "insetPixels":
                return "insetPixels";
            default:
                return "fraction";
        }
    }
    else {
        return "fraction";
    }
}
function readIconState(text) {
    if (text !== null) {
        switch (text) {
            case "open":
                return IconState.Open;
            case "closed":
                return IconState.Closed;
            case "error":
                return IconState.Error;
            case "fetching0":
                return IconState.Fetching0;
            case "fetching1":
                return IconState.Fetching1;
            case "fetching2":
                return IconState.Fetching2;
            default:
                return IconState.Open;
        }
    }
    else {
        return IconState.Open;
    }
}
function readStyleState(text) {
    if (text !== null) {
        switch (text) {
            case "normal":
                return StyleState.Normal;
            case "highlight":
                return StyleState.Highlight;
            default:
                return StyleState.Normal;
        }
    }
    else {
        return StyleState.Normal;
    }
}
function readFloat(text) {
    if (text !== null) {
        return assertNaN(parseFloat(text.trim()));
    }
    else {
        throw new Error("KML boolean field textContent empty");
    }
}
function findChildElementByName(parent, name) {
    for (let i = 0; i < parent.children.length; i += 1) {
        const child = parent.children.item(i);
        if (child === null)
            continue;
        if (child.tagName === name)
            return child;
    }
    return null;
}
function elementTypeFromTagName(element) {
    switch (element.tagName) {
        case "Document":
            return KMLElementType.Document;
        case "Folder":
            return KMLElementType.Folder;
        case "Placemark":
            return KMLElementType.Placemark;
        case "Point":
            return KMLElementType.Point;
        case "LineString":
            return KMLElementType.LineString;
        case "LinearRing":
            return KMLElementType.LinearRing;
        case "Polygon":
            return KMLElementType.Polygon;
        case "MultiGeometry":
            return KMLElementType.MultiGeometry;
        case "Style":
            return KMLElementType.Style;
        case "StyleMap":
            return KMLElementType.StyleMap;
        case "LineStyle":
            return KMLElementType.LineStyle;
        case "PolyStyle":
            return KMLElementType.PolyStyle;
        case "IconStyle":
            return KMLElementType.IconStyle;
        case "LabelStyle":
            return KMLElementType.LabelStyle;
        case "BalloonStyle":
            return KMLElementType.BalloonStyle;
        case "ListStyle":
            return KMLElementType.ListStyle;
        default:
            return undefined;
    }
}
function isFeatureElement(elementType) {
    if (isContainerElement(elementType))
        return true;
    switch (elementType) {
        case KMLElementType.Placemark:
            return true;
        default:
            return false;
    }
}
function isContainerElement(elementType) {
    switch (elementType) {
        case KMLElementType.Document:
        case KMLElementType.Folder:
            return true;
        default:
            return false;
    }
}
function isGeometryElement(elementType) {
    switch (elementType) {
        case KMLElementType.Point:
        case KMLElementType.LineString:
        case KMLElementType.LinearRing:
        case KMLElementType.Polygon:
        case KMLElementType.MultiGeometry:
            return true;
        default:
            return false;
    }
}
function isStyleSelectorElement(elementType) {
    switch (elementType) {
        case KMLElementType.Style:
        case KMLElementType.StyleMap:
            return true;
        default:
            return false;
    }
}
/**
 * A disgusting hack borrowed from cesium for dealing with shitty user data. Inserts namespaces that are commonly
 * incorrectly missing in user data.
 * @param text
 */
function insertNamespaces(text) {
    const namespaceMap = {
        xsi: "http://www.w3.org/2001/XMLSchema-instance",
    };
    let firstPart;
    let lastPart;
    for (const key in namespaceMap) {
        if (namespaceMap.hasOwnProperty(key)) {
            const reg = RegExp("[< ]" + key + ":");
            const declaration = "xmlns:" + key + "=";
            if (reg.test(text) && text.indexOf(declaration) === -1) {
                if (firstPart === undefined || firstPart === null) {
                    firstPart = text.substr(0, text.indexOf("<kml") + 4);
                    lastPart = text.substr(firstPart.length);
                }
                firstPart += " " + declaration + '"' + namespaceMap[key] + '"';
            }
        }
    }
    if (firstPart !== undefined && firstPart !== null) {
        text = firstPart + lastPart;
    }
    return text;
}
function assertNaN(v) {
    if (OptionsManager.DEBUG_KML_NAN_ASSERT) {
        if (isNaN(v)) {
            throw new Error("NaN");
        }
        else
            return v;
    }
    else {
        return v;
    }
}
